import { ChangeDetectionStrategy, Component, computed, DOCUMENT, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatListModule } from '@angular/material/list';
import { RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { DashboardProfileContextService, DashboardProfileUserContextService } from '@rma/dashboard/authentication/public/user';
import { CurrentSubscriptionService } from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { StaticImageUrlPipe } from '@rma/dashboard/generic/util/pipes/static-image-url';
import { TypeGuardPipe } from '@rma/dashboard/generic/util/pipes/type-guard';
import { LG_RENOWNED_LOGO_PATH, SM_RENOWNED_LOGO_PATH } from '@rma/dashboard/layout/public/util-layout';
import { ProfileBrandingService } from '@rma/dashboard/profile/public/api-branding';
import { ProfileDetailsContextService } from '@rma/dashboard/profile/public/api-profile-details';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { map } from 'rxjs';
import { isItem, isMenu } from '../domain/entities/side-nav-items';
import { SideNavigationService } from '../domain/services/side-navigation.service';
import { AccountsFooterComponent } from '../ui-nav-accounts-footer/nav-accounts-footer.component';
import { NavMenuItemComponent } from '../ui-nav-menu-item/nav-menu-item.component';
import { NavProfileMenuComponent } from '../ui-nav-profile-menu/nav-profile-menu.component';
import { SubMenuComponent } from '../ui-sub-menu/sub-menu.component';
import { IsNavMenuItemActivePipe } from '../util-is-nav-menu-item-active/is-nav-menu-item-active.pipe';
import { IsSubMenuOpenPipe } from '../util-is-sub-menu-open/is-sub-menu-open.pipe';

@Component({
  selector: 'rma-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  imports: [
    AccountsFooterComponent,
    IsNavMenuItemActivePipe,
    IsSubMenuOpenPipe,
    MatListModule,
    NavMenuItemComponent,
    NavProfileMenuComponent,
    RmaIconComponent,
    RouterModule,
    StaticImageUrlPipe,
    SubMenuComponent,
    TranslocoModule,
    TypeGuardPipe,
  ],
  host: {
    '[class.mobile-nav-visible]': 'visible()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope('sideNav')],
})
export class SideNavComponent {
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly document = inject(DOCUMENT);
  private readonly profileBrandingService = inject(ProfileBrandingService);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly profileDetailsService = inject(ProfileDetailsContextService);
  private readonly profileUserContextService = inject(DashboardProfileUserContextService);
  private readonly sideNavigationService = inject(SideNavigationService);

  protected readonly isMenu = isMenu;
  protected readonly isItem = isItem;

  protected readonly profileContext = toSignal(this.profileContextService.profileContext$);

  protected readonly currentPlanName = toSignal(
    this.currentSubscriptionService.currentSubscriptionSummary$.pipe(
      map((currentSubscription) => currentSubscription?.displayName ?? null),
    ),
    { initialValue: null },
  );

  //This should be cleaned up when some one has times as this is one of the only places that uses this
  protected readonly profileUser = toSignal(this.profileUserContextService.profileUserContext$);

  protected readonly profileRating = toSignal(
    this.profileDetailsService.profileSummary$.pipe(map(({ ratingValue, reviewCount }) => ({ ratingValue, reviewCount }))),
    { initialValue: null },
  );

  protected readonly agencyName = computed(() => this.profileContext()?.parentName ?? null);

  protected readonly brandPrimaryColour = toSignal(this.profileBrandingService.branding$.pipe(map(({ primaryColour }) => primaryColour)), {
    initialValue: null,
  });

  protected readonly currentUrl = toSignal(this.sideNavigationService.currentUrl$);
  protected readonly navigation = toSignal(this.sideNavigationService.navigation$, { initialValue: [] });

  protected readonly lgRenownedLogoPath = LG_RENOWNED_LOGO_PATH;
  protected readonly smRenownedLogoPath = SM_RENOWNED_LOGO_PATH;

  public readonly visible = input(false);
  public readonly collapsed = input(false);

  public readonly navigated = output<void>();
  public readonly subMenuExpanded = output<void>();
  public readonly toggleSideNav = output<boolean>();

  public onNavigate(): void {
    this.navigated.emit();
  }

  public onMenuExpanded(id: string) {
    if (this.collapsed()) {
      this.subMenuExpanded.emit();

      setTimeout(() => {
        const subMenu = this.document.querySelector(`[data-id='${id}']`);

        if (subMenu) {
          subMenu.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }, 300);
    }
  }
}
